import React from 'react';
import { Inspector, Tabs } from '@bynder/design-system';
import { InspectorSetting } from '../../../../constants/guides';
import { GuideSettings } from './GuideSettings';
import { PageSettings } from '../PageSettings/PageSettings';
import { PageInspectorFooter } from '../../../pages/PageInspectorFooter';
import { GuideInspectorFooter } from './GuideInspectorFooter';
import { StyledTabsShadow } from '../../Styles';
import { useInspectorStore } from '../../../../stores/inspectorStore';
import {
  HomePageSettings,
  LandingPageSettings,
} from '../SinglePageSettings/SinglePageSettings';
import { useActiveGuide, useActivePage } from '../../../../queries/guides';
import { Guide, GuideType, Page, PageStates } from '../../../../types';

import { useCanEditHomepage } from '../../../../helpers/hooks';

export const GuidePageSettings: React.FC = () => {
  const { data: guide } = useActiveGuide();
  const { data: page } = useActivePage();

  const canEditHomepage = useCanEditHomepage();
  if (!guide || !page) {
    return null;
  }

  const isLandingPage = guide.type === GuideType.SINGLE_PAGE;
  const isHomepage = guide.type === GuideType.HOME_PAGE;

  if (isHomepage && canEditHomepage) {
    const defaultHomepage =
      guide.chapters[0].pages[guide.chapters[0].pages.length - 1];
    const isLastHomepagePageActive = page.id === defaultHomepage.id;

    return (
      <HomePageSettings
        defaultPageId={defaultHomepage.id}
        guideId={guide.id}
        pageId={page.id}
        guideTitle={guide.title}
        pageTitle={page.title}
        isActivePagePublished={page.state === PageStates.PUBLISHED}
        isLastHomepagePageActive={isLastHomepagePageActive}
        targetedPagesEnabled={guide.targetedPagesEnabled}
      />
    );
  }

  if (isLandingPage) {
    return (
      <LandingPageSettings
        isPublished={page.state === PageStates.PUBLISHED}
        guideId={guide.id}
        guideTitle={guide.title}
      />
    );
  }

  return <InspectorGuidePageSettings guide={guide} page={page} />;
};

const InspectorGuidePageSettings: React.FC<{ guide: Guide; page: Page }> = ({
  guide,
  page,
}) => {
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );
  const activeInspectorSetting = useInspectorStore(
    s => s.activeInspectorSetting,
  );
  return (
    <>
      <StyledTabsShadow>
        <Inspector.Tabs>
          <Tabs>
            <Tabs.Tab
              label="Guide"
              data-testid="guideInspectorTab"
              isActive={activeInspectorSetting === InspectorSetting.GUIDE_VIEW}
              onClick={() => {
                setActiveInspectorSetting(InspectorSetting.GUIDE_VIEW);
              }}
            />
            <Tabs.Tab
              data-testid="pageInspectorTab"
              label="Page"
              isActive={activeInspectorSetting === InspectorSetting.PAGE_VIEW}
              onClick={() => {
                setActiveInspectorSetting(InspectorSetting.PAGE_VIEW);
              }}
            />
          </Tabs>
        </Inspector.Tabs>
      </StyledTabsShadow>
      <Inspector.Content>
        {activeInspectorSetting === InspectorSetting.GUIDE_VIEW ? (
          <GuideSettings guide={guide} />
        ) : (
          <PageSettings page={page} />
        )}
      </Inspector.Content>
      <Inspector.Footer>
        {activeInspectorSetting === InspectorSetting.GUIDE_VIEW ? (
          <GuideInspectorFooter />
        ) : (
          <PageInspectorFooter
            guideOwnerId={guide.ownerId}
            pageOwnerId={page.ownerId}
            pageId={page.id}
            guideId={guide.id}
            pageTitle={page.title}
            canEditGuide={guide.canEdit}
          />
        )}
      </Inspector.Footer>
    </>
  );
};
